import { useCallback, useEffect, useMemo, useState } from "react";
import StatusBadge from "../../intelligence/StatusBadge";
import {
  getAnalyticsSummary,
  getAnalyticsTraffic,
  getAnalyticsSearches,
  getAnalyticsListings,
  getAnalyticsLeads,
  getAnalyticsConversions,
  getAnalyticsMarket,
  getAnalyticsCampaigns,
  getAnalyticsTimeOnMarket,
  rebuildAnalytics,
  exportAnalyticsReport,
  getGa4AnalyticsStatus,
  getGa4AnalyticsSummary,
  getGa4AnalyticsTraffic,
  getGa4AnalyticsEngagement,
  syncGa4Analytics,
  updateGa4AnalyticsSettings,
} from "../../../../api/adminClientViewAnalyticsApi";
import StudioGa4ConnectModal from "./StudioGa4ConnectModal";

const DEFAULT_ANALYTICS_API = {
  getAnalyticsSummary,
  getAnalyticsTraffic,
  getAnalyticsSearches,
  getAnalyticsListings,
  getAnalyticsLeads,
  getAnalyticsConversions,
  getAnalyticsMarket,
  getAnalyticsCampaigns,
  getAnalyticsTimeOnMarket,
  rebuildAnalytics,
  exportAnalyticsReport,
  getGa4AnalyticsStatus,
  getGa4AnalyticsSummary,
  getGa4AnalyticsTraffic,
  getGa4AnalyticsEngagement,
  syncGa4Analytics,
  updateGa4AnalyticsSettings,
};

const SUB_TABS = [
  { id: "traffic", label: "Traffic" },
  { id: "engagement", label: "Engagement" },
  { id: "properties", label: "Properties" },
  { id: "leads", label: "Leads" },
];

const RANGE_PRESETS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
  { label: "1y", days: 365 },
];

function defaultRange(days = 30) {
  const end = new Date();
  const start = new Date(end.getTime() - days * 24 * 60 * 60 * 1000);
  return { startDate: start.toISOString().slice(0, 10), endDate: end.toISOString().slice(0, 10) };
}

function fmt(val) {
  if (val == null) return "—";
  return Number(val).toLocaleString();
}

function pct(val) {
  if (val == null) return "—";
  return `${(val * 100).toFixed(1)}%`;
}

function currency(val) {
  if (val == null) return "—";
  return "$" + Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

/* ── Metric card ── */

function MetricCard({ label, value, sub, tone }) {
  return (
    <div className="cv-analytics__metric">
      <span className="cv-analytics__metric-label">{label}</span>
      <span className={`cv-analytics__metric-value${tone ? ` cv-analytics__metric-value--${tone}` : ""}`}>{value}</span>
      {sub ? <span className="cv-analytics__metric-sub">{sub}</span> : null}
    </div>
  );
}

/* ── Mini bar ── */

function MiniBar({ value, max, color }) {
  const pctVal = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="cv-analytics__minibar-track">
      <div className="cv-analytics__minibar-fill" style={{ width: `${pctVal}%`, background: color || undefined }} />
    </div>
  );
}

function SourceTag({ label, tone = "midpoint" }) {
  return <span className={`cv-analytics__source-tag cv-analytics__source-tag--${tone}`}>{label}</span>;
}

function CompareMetricRow({ label, midpointValue, ga4Value }) {
  const midpoint = Number(midpointValue) || 0;
  const ga4 = Number(ga4Value) || 0;
  const max = Math.max(midpoint, ga4, 1);

  return (
    <div className="cv-analytics__compare-row">
      <div className="cv-analytics__compare-label">{label}</div>
      <div className="cv-analytics__compare-col">
        <SourceTag label="Midpoint" tone="midpoint" />
        <span className="cv-analytics__compare-value">{fmt(midpointValue)}</span>
        <MiniBar value={midpoint} max={max} color="#0f2744" />
      </div>
      <div className="cv-analytics__compare-col">
        <SourceTag label="GA4" tone="ga4" />
        <span className="cv-analytics__compare-value">{fmt(ga4Value)}</span>
        <MiniBar value={ga4} max={max} color="#2563eb" />
      </div>
    </div>
  );
}

function RankList({ title, items, emptyText, sourceTone = "midpoint" }) {
  const maxCount = Math.max(...(items || []).map((item) => item.count), 1);
  return (
    <section className="cv-analytics__card">
      <h4 className="cv-analytics__card-title">
        {title} <SourceTag label={sourceTone === "ga4" ? "GA4" : "Midpoint"} tone={sourceTone} />
      </h4>
      {!items?.length ? (
        <p className="cv-analytics__empty-text">{emptyText}</p>
      ) : (
        <div className="cv-analytics__rank-list">
          {items.slice(0, 10).map((item, index) => (
            <div key={`${item.pagePath || item.label || item.eventName}-${index}`} className="cv-analytics__rank-item">
              <span className="cv-analytics__rank-num">{index + 1}</span>
              <div className="cv-analytics__rank-info">
                <span className="cv-analytics__rank-label">
                  {item.pagePath || item.label || item.eventName || "(not set)"}
                </span>
                <MiniBar
                  value={item.count}
                  max={maxCount}
                  color={sourceTone === "ga4" ? "#2563eb" : undefined}
                />
              </div>
              <span className="cv-analytics__rank-value">{fmt(item.count)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function CombinedTrafficPane({ data }) {
  const midpointSummary = data?.midpoint?.summary;
  const ga4Summary = data?.ga4?.summary;
  const midpointPages = data?.midpoint?.traffic?.topPages || [];
  const ga4Pages = data?.ga4?.traffic?.topPages || [];
  const midpointSources = data?.midpoint?.traffic?.utmBreakdown || [];
  const ga4Sources = data?.ga4?.traffic?.utmBreakdown || [];

  return (
    <div className="cv-analytics__pane">
      <section className="cv-analytics__card cv-analytics__card--compare">
        <h4 className="cv-analytics__card-title">Traffic comparison</h4>
        <CompareMetricRow label="Page views" midpointValue={midpointSummary?.pageViews} ga4Value={ga4Summary?.pageViews} />
        <CompareMetricRow label="Unique visitors" midpointValue={midpointSummary?.uniqueVisitors} ga4Value={ga4Summary?.uniqueVisitors} />
        <CompareMetricRow label="Sessions" midpointValue={midpointSummary?.uniqueSessions} ga4Value={ga4Summary?.uniqueSessions} />
        <CompareMetricRow label="Searches" midpointValue={midpointSummary?.searches} ga4Value={ga4Summary?.searches} />
      </section>

      <div className="cv-analytics__two-col">
        <RankList
          title="Top pages"
          items={midpointPages.map((page) => ({ pagePath: page.pagePath, count: page.count }))}
          emptyText="No Midpoint page data for this period."
          sourceTone="midpoint"
        />
        <RankList
          title="Top pages"
          items={ga4Pages.map((page) => ({ pagePath: page.pagePath, count: page.count }))}
          emptyText="No GA4 page data for this period. Sync GA4 first."
          sourceTone="ga4"
        />
      </div>

      <div className="cv-analytics__two-col">
        <RankList
          title="Traffic sources"
          items={midpointSources.map((source) => ({
            label: `${source.source || "direct"} / ${source.medium || "none"}`,
            count: source.count,
          }))}
          emptyText="No Midpoint UTM data for this period."
          sourceTone="midpoint"
        />
        <RankList
          title="Traffic sources"
          items={ga4Sources.map((source) => ({
            label: `${source.source || "direct"} / ${source.medium || "none"}`,
            count: source.count,
          }))}
          emptyText="No GA4 source data for this period."
          sourceTone="ga4"
        />
      </div>
    </div>
  );
}

function CombinedEngagementPane({ data }) {
  const midpointSummary = data?.midpoint?.summary;
  const ga4Summary = data?.ga4?.engagement?.summary || data?.ga4?.summary;
  const midpointQueries = data?.midpoint?.searches?.topQueries || [];
  const ga4Events = data?.ga4?.engagement?.topEvents || [];

  return (
    <div className="cv-analytics__pane">
      <section className="cv-analytics__card cv-analytics__card--compare">
        <h4 className="cv-analytics__card-title">Engagement comparison</h4>
        <CompareMetricRow label="Searches" midpointValue={midpointSummary?.searches} ga4Value={ga4Summary?.searches} />
        <CompareMetricRow label="Listing views" midpointValue={midpointSummary?.listingViews} ga4Value={ga4Summary?.listingViews} />
        <CompareMetricRow label="Saved listings" midpointValue={midpointSummary?.savedListings} ga4Value={ga4Summary?.savedListings} />
        <CompareMetricRow label="Leads" midpointValue={midpointSummary?.leads} ga4Value={ga4Summary?.leads} />
      </section>

      <div className="cv-analytics__two-col">
        <RankList
          title="Top searches"
          items={midpointQueries.map((query) => ({
            label: query.query || "(empty)",
            count: query.searchCount,
          }))}
          emptyText="No Midpoint search data for this period."
          sourceTone="midpoint"
        />
        <RankList
          title="Top GA4 events"
          items={ga4Events.map((event) => ({
            label: event.label || event.eventName,
            count: event.count,
          }))}
          emptyText="No GA4 event data for this period."
          sourceTone="ga4"
        />
      </div>
    </div>
  );
}

function CombinedSourceNote({ children }) {
  return <p className="cv-analytics__combined-note">{children}</p>;
}

/* ── Traffic tab ── */

function TrafficPane({ data }) {
  const summary = data?.summary;
  const traffic = data?.traffic;
  const topPages = traffic?.topPages || [];
  const utmBreakdown = traffic?.utmBreakdown || [];
  const maxPageCount = Math.max(...topPages.map((p) => p.count), 1);

  return (
    <div className="cv-analytics__pane">
      {/* KPI row */}
      <div className="cv-analytics__kpi-row">
        <MetricCard label="Page Views" value={fmt(summary?.pageViews)} />
        <MetricCard label="Unique Visitors" value={fmt(summary?.uniqueVisitors)} />
        <MetricCard label="Sessions" value={fmt(summary?.uniqueSessions)} />
        <MetricCard label="Searches" value={fmt(summary?.searches)} />
      </div>

      <div className="cv-analytics__two-col">
        {/* Top pages */}
        <section className="cv-analytics__card">
          <h4 className="cv-analytics__card-title">Top Pages</h4>
          {topPages.length === 0 ? <p className="cv-analytics__empty-text">No page data for this period.</p> : (
            <div className="cv-analytics__rank-list">
              {topPages.slice(0, 10).map((p, i) => (
                <div key={p.pagePath} className="cv-analytics__rank-item">
                  <span className="cv-analytics__rank-num">{i + 1}</span>
                  <div className="cv-analytics__rank-info">
                    <span className="cv-analytics__rank-label">{p.pagePath}</span>
                    <MiniBar value={p.count} max={maxPageCount} />
                  </div>
                  <span className="cv-analytics__rank-value">{fmt(p.count)}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* UTM sources */}
        <section className="cv-analytics__card">
          <h4 className="cv-analytics__card-title">Traffic Sources</h4>
          {utmBreakdown.length === 0 ? <p className="cv-analytics__empty-text">No UTM data for this period.</p> : (
            <div className="cv-analytics__rank-list">
              {utmBreakdown.slice(0, 10).map((u, i) => (
                <div key={`${u.source}-${u.medium}-${i}`} className="cv-analytics__rank-item">
                  <span className="cv-analytics__rank-num">{i + 1}</span>
                  <div className="cv-analytics__rank-info">
                    <span className="cv-analytics__rank-label">{u.source || "direct"} / {u.medium || "none"}</span>
                  </div>
                  <span className="cv-analytics__rank-value">{fmt(u.count)}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

/* ── GA4 Engagement tab ── */

function Ga4EngagementPane({ data }) {
  const summary = data?.engagement?.summary || data?.summary;
  const topEvents = data?.engagement?.topEvents || [];
  const funnel = data?.engagement?.funnel || [];
  const maxEventCount = Math.max(...topEvents.map((event) => event.count), 1);
  const maxFunnelCount = Math.max(...funnel.map((step) => step.count), 1);

  return (
    <div className="cv-analytics__pane">
      <div className="cv-analytics__kpi-row">
        <MetricCard label="Searches" value={fmt(summary?.searches)} />
        <MetricCard label="Listing Views" value={fmt(summary?.listingViews)} />
        <MetricCard label="Saved Listings" value={fmt(summary?.savedListings)} />
        <MetricCard label="Leads" value={fmt(summary?.leads)} />
      </div>

      <div className="cv-analytics__two-col">
        <section className="cv-analytics__card">
          <h4 className="cv-analytics__card-title">GA4 Events</h4>
          {topEvents.length === 0 ? (
            <p className="cv-analytics__empty-text">No GA4 event data for this period. Sync GA4 first.</p>
          ) : (
            <div className="cv-analytics__rank-list">
              {topEvents.slice(0, 12).map((event, index) => (
                <div key={event.eventName} className="cv-analytics__rank-item">
                  <span className="cv-analytics__rank-num">{index + 1}</span>
                  <div className="cv-analytics__rank-info">
                    <span className="cv-analytics__rank-label">{event.label || event.eventName}</span>
                    <MiniBar value={event.count} max={maxEventCount} color="#2563eb" />
                  </div>
                  <span className="cv-analytics__rank-value">{fmt(event.count)}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="cv-analytics__card">
          <h4 className="cv-analytics__card-title">Engagement Funnel</h4>
          {funnel.length === 0 ? (
            <p className="cv-analytics__empty-text">No funnel data.</p>
          ) : (
            <div className="cv-analytics__funnel">
              {funnel.map((step, index) => {
                const width = Math.max(Math.round((step.count / maxFunnelCount) * 100), 8);
                return (
                  <div key={step.eventName} className="cv-analytics__funnel-step">
                    <div className="cv-analytics__funnel-bar" style={{ width: `${width}%` }}>
                      <span className="cv-analytics__funnel-label">{step.stepName}</span>
                      <span className="cv-analytics__funnel-count">{fmt(step.count)}</span>
                    </div>
                    {index < funnel.length - 1 ? <div className="cv-analytics__funnel-arrow" /> : null}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

/* ── Engagement tab ── */

function EngagementPane({ data }) {
  const summary = data?.summary;
  const searches = data?.searches;
  const conversions = data?.conversions;
  const topQueries = searches?.topQueries || [];
  const insights = searches?.insights || [];
  const steps = conversions?.steps || [];

  return (
    <div className="cv-analytics__pane">
      <div className="cv-analytics__kpi-row">
        <MetricCard label="Searches" value={fmt(summary?.searches)} />
        <MetricCard label="Listing Views" value={fmt(summary?.listingViews)} />
        <MetricCard label="Saved Listings" value={fmt(summary?.savedListings)} />
        <MetricCard label="Saved Searches" value={fmt(summary?.savedSearches)} />
      </div>

      <div className="cv-analytics__two-col">
        {/* Top searches */}
        <section className="cv-analytics__card">
          <h4 className="cv-analytics__card-title">Top Searches</h4>
          {topQueries.length === 0 ? <p className="cv-analytics__empty-text">No search data.</p> : (
            <div className="cv-analytics__rank-list">
              {topQueries.slice(0, 10).map((q, i) => (
                <div key={`${q.query}-${i}`} className="cv-analytics__rank-item">
                  <span className="cv-analytics__rank-num">{i + 1}</span>
                  <div className="cv-analytics__rank-info">
                    <span className="cv-analytics__rank-label">{q.query || "(empty)"}</span>
                    {q.zeroResultCount > 0 ? <span className="cv-analytics__rank-warn">{q.zeroResultCount} zero results</span> : null}
                  </div>
                  <span className="cv-analytics__rank-value">{fmt(q.searchCount)}</span>
                </div>
              ))}
            </div>
          )}
          {insights.length > 0 ? (
            <div className="cv-analytics__insights">
              <h5 className="cv-analytics__insights-title">Insights</h5>
              {insights.map((ins, i) => (
                <p key={i} className="cv-analytics__insight-item">{ins.message}</p>
              ))}
            </div>
          ) : null}
        </section>

        {/* Conversion funnel */}
        <section className="cv-analytics__card">
          <h4 className="cv-analytics__card-title">Conversion Funnel</h4>
          {steps.length === 0 ? <p className="cv-analytics__empty-text">No funnel data.</p> : (
            <div className="cv-analytics__funnel">
              {steps.map((step, i) => {
                const maxSessions = steps[0]?.sessions || 1;
                const width = Math.max(Math.round((step.sessions / maxSessions) * 100), 8);
                return (
                  <div key={step.stepName} className="cv-analytics__funnel-step">
                    <div className="cv-analytics__funnel-bar" style={{ width: `${width}%` }}>
                      <span className="cv-analytics__funnel-label">{step.stepName}</span>
                      <span className="cv-analytics__funnel-count">{fmt(step.sessions)}</span>
                    </div>
                    {i < steps.length - 1 ? <div className="cv-analytics__funnel-arrow" /> : null}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

/* ── Properties tab ── */

function PropertiesPane({ data }) {
  const listings = data?.listings?.listings || [];
  const market = data?.market;
  const tom = data?.market?.timeOnMarket;
  const byListingType = market?.byListingType || [];

  return (
    <div className="cv-analytics__pane">
      <div className="cv-analytics__kpi-row">
        <MetricCard label="Active Listings" value={fmt(market?.activeListings)} />
        <MetricCard label="Median Time on Market" value={tom?.observedTimeOnMarket?.medianDays != null ? `${tom.observedTimeOnMarket.medianDays}d` : "—"} />
        <MetricCard label="Stale Listings" value={fmt(tom?.staleListingCount)} sub={tom?.staleThresholdDays ? `>${tom.staleThresholdDays} days` : undefined} tone={tom?.staleListingCount > 0 ? "warning" : undefined} />
        <MetricCard label="Listing Types" value={fmt(byListingType.length)} />
      </div>

      <div className="cv-analytics__two-col">
        {/* Market breakdown */}
        <section className="cv-analytics__card">
          <h4 className="cv-analytics__card-title">By Listing Type</h4>
          {byListingType.length === 0 ? <p className="cv-analytics__empty-text">No market data.</p> : (
            <div className="cv-analytics__table-wrapper">
              <table className="cv-analytics__table">
                <thead>
                  <tr><th>Type</th><th>Count</th><th>Median Price</th></tr>
                </thead>
                <tbody>
                  {byListingType.map((row) => (
                    <tr key={row.listingType}>
                      <td>{row.listingType || "—"}</td>
                      <td>{fmt(row.count)}</td>
                      <td>{currency(row.medianPrice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Top listings */}
        <section className="cv-analytics__card">
          <h4 className="cv-analytics__card-title">Top Listings by Engagement</h4>
          {listings.length === 0 ? <p className="cv-analytics__empty-text">No listing engagement data.</p> : (
            <div className="cv-analytics__rank-list">
              {listings.slice(0, 10).map((row, i) => (
                <div key={row.canonicalListingId} className="cv-analytics__rank-item">
                  <span className="cv-analytics__rank-num">{i + 1}</span>
                  <div className="cv-analytics__rank-info">
                    <span className="cv-analytics__rank-label">{row.title || row.canonicalListingId}</span>
                    <span className="cv-analytics__rank-meta">{row.listingViews} views &middot; {row.saves} saves &middot; {row.leadCount} leads</span>
                  </div>
                  <span className="cv-analytics__rank-value">{row.engagementScore}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

/* ── Leads tab ── */

function LeadsPane({ data }) {
  const summary = data?.summary;
  const leads = data?.leads;
  const byLeadType = leads?.byLeadType || [];
  const byListing = leads?.byListing || [];
  const conversion = leads?.conversion;
  const campaigns = data?.campaigns?.campaigns || [];
  const maxLeadType = Math.max(...byLeadType.map((r) => r.count), 1);

  return (
    <div className="cv-analytics__pane">
      <div className="cv-analytics__kpi-row">
        <MetricCard label="Total Leads" value={fmt(summary?.leads)} />
        <MetricCard label="Opportunities" value={fmt(summary?.opportunities)} />
        <MetricCard label="Conversion Rate" value={pct(summary?.leadConversionRate)} tone={summary?.leadConversionRate > 0.05 ? "green" : undefined} />
        <MetricCard label="Lead → Opp" value={conversion ? `${conversion.opportunities}/${conversion.leads}` : "—"} />
      </div>

      <div className="cv-analytics__two-col">
        {/* By lead type */}
        <section className="cv-analytics__card">
          <h4 className="cv-analytics__card-title">By Lead Type</h4>
          {byLeadType.length === 0 ? <p className="cv-analytics__empty-text">No lead type data.</p> : (
            <div className="cv-analytics__rank-list">
              {byLeadType.map((r, i) => (
                <div key={r.leadType || i} className="cv-analytics__rank-item">
                  <span className="cv-analytics__rank-num">{i + 1}</span>
                  <div className="cv-analytics__rank-info">
                    <span className="cv-analytics__rank-label">{r.leadType || "unknown"}</span>
                    <MiniBar value={r.count} max={maxLeadType} color="#16a34a" />
                  </div>
                  <span className="cv-analytics__rank-value">{fmt(r.count)}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* By listing */}
        <section className="cv-analytics__card">
          <h4 className="cv-analytics__card-title">Leads by Listing</h4>
          {byListing.length === 0 ? <p className="cv-analytics__empty-text">No per-listing lead data.</p> : (
            <div className="cv-analytics__rank-list">
              {byListing.slice(0, 10).map((r, i) => (
                <div key={r.canonicalListingId || i} className="cv-analytics__rank-item">
                  <span className="cv-analytics__rank-num">{i + 1}</span>
                  <div className="cv-analytics__rank-info">
                    <span className="cv-analytics__rank-label">{r.title || r.canonicalListingId}</span>
                  </div>
                  <span className="cv-analytics__rank-value">{fmt(r.count)}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Campaigns */}
      {campaigns.length > 0 ? (
        <section className="cv-analytics__card">
          <h4 className="cv-analytics__card-title">Campaigns</h4>
          <div className="cv-analytics__table-wrapper">
            <table className="cv-analytics__table">
              <thead>
                <tr><th>Campaign</th><th>Impressions</th><th>Clicks</th><th>CTR</th></tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.campaignId}>
                    <td>{c.campaignId}</td>
                    <td>{fmt(c.impressions)}</td>
                    <td>{fmt(c.clicks)}</td>
                    <td>{pct(c.ctr)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}

/* ── Main component ── */

const StudioAnalyticsSection = ({
  clientViewId,
  disabled,
  analyticsApi = DEFAULT_ANALYTICS_API,
  initialDataSource = "midpoint",
  visibleTabIds = null,
}) => {
  const subTabs = useMemo(
    () => (visibleTabIds ? SUB_TABS.filter((tab) => visibleTabIds.includes(tab.id)) : SUB_TABS),
    [visibleTabIds]
  );
  const [activeSubTab, setActiveSubTab] = useState(() => subTabs[0]?.id || "traffic");
  const [dataSource, setDataSource] = useState(initialDataSource);
  const [range, setRange] = useState(() => defaultRange(30));
  const [activePreset, setActivePreset] = useState("30d");
  const [allData, setAllData] = useState({});
  const [ga4Status, setGa4Status] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [message, setMessage] = useState("");
  const [rebuilding, setRebuilding] = useState(false);
  const [syncingGa4, setSyncingGa4] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [ga4ModalOpen, setGa4ModalOpen] = useState(false);
  const [savingGa4, setSavingGa4] = useState(false);
  const [ga4ModalError, setGa4ModalError] = useState("");

  const loadGa4Status = useCallback(async () => {
    try {
      const body = await analyticsApi.getGa4AnalyticsStatus(clientViewId);
      setGa4Status(body.dataSource || null);
    } catch {
      setGa4Status(null);
    }
  }, [analyticsApi, clientViewId]);

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");
    const params = { startDate: range.startDate, endDate: range.endDate };
    try {
      if (dataSource === "ga4") {
        const [summary, traffic, engagement] = await Promise.all([
          analyticsApi.getGa4AnalyticsSummary(clientViewId, params).catch(() => ({})),
          analyticsApi.getGa4AnalyticsTraffic(clientViewId, params).catch(() => ({})),
          analyticsApi.getGa4AnalyticsEngagement(clientViewId, params).catch(() => ({})),
        ]);
        setAllData({
          summary: summary?.summary || summary,
          traffic,
          engagement,
        });
      } else if (dataSource === "combined") {
        const [
          midpointSummary,
          midpointTraffic,
          midpointSearches,
          midpointListings,
          midpointLeads,
          midpointConversions,
          midpointMarket,
          midpointTom,
          midpointCampaigns,
          ga4Summary,
          ga4Traffic,
          ga4Engagement,
        ] = await Promise.all([
          analyticsApi.getAnalyticsSummary(clientViewId, params).catch(() => ({})),
          analyticsApi.getAnalyticsTraffic(clientViewId, params).catch(() => ({})),
          analyticsApi.getAnalyticsSearches(clientViewId, params).catch(() => ({})),
          analyticsApi.getAnalyticsListings(clientViewId, params).catch(() => ({})),
          analyticsApi.getAnalyticsLeads(clientViewId, params).catch(() => ({})),
          analyticsApi.getAnalyticsConversions(clientViewId, params).catch(() => ({})),
          analyticsApi.getAnalyticsMarket(clientViewId, params).catch(() => ({})),
          analyticsApi.getAnalyticsTimeOnMarket(clientViewId, params).catch(() => ({})),
          analyticsApi.getAnalyticsCampaigns(clientViewId, params).catch(() => ({})),
          analyticsApi.getGa4AnalyticsSummary(clientViewId, params).catch(() => ({})),
          analyticsApi.getGa4AnalyticsTraffic(clientViewId, params).catch(() => ({})),
          analyticsApi.getGa4AnalyticsEngagement(clientViewId, params).catch(() => ({})),
        ]);

        setAllData({
          midpoint: {
            summary: midpointSummary?.summary || midpointSummary,
            freshness: midpointSummary?.freshness,
            traffic: midpointTraffic,
            searches: midpointSearches,
            listings: { listings: midpointListings?.listings || [] },
            leads: midpointLeads,
            conversions: midpointConversions,
            market: { ...midpointMarket, timeOnMarket: midpointTom },
            campaigns: midpointCampaigns,
          },
          ga4: {
            summary: ga4Summary?.summary || ga4Summary,
            traffic: ga4Traffic,
            engagement: ga4Engagement,
          },
        });
      } else {
        const [summary, traffic, searches, listings, leads, conversions, market, tom, campaigns] =
          await Promise.all([
            analyticsApi.getAnalyticsSummary(clientViewId, params).catch(() => ({})),
            analyticsApi.getAnalyticsTraffic(clientViewId, params).catch(() => ({})),
            analyticsApi.getAnalyticsSearches(clientViewId, params).catch(() => ({})),
            analyticsApi.getAnalyticsListings(clientViewId, params).catch(() => ({})),
            analyticsApi.getAnalyticsLeads(clientViewId, params).catch(() => ({})),
            analyticsApi.getAnalyticsConversions(clientViewId, params).catch(() => ({})),
            analyticsApi.getAnalyticsMarket(clientViewId, params).catch(() => ({})),
            analyticsApi.getAnalyticsTimeOnMarket(clientViewId, params).catch(() => ({})),
            analyticsApi.getAnalyticsCampaigns(clientViewId, params).catch(() => ({})),
          ]);
        setAllData({
          summary: summary?.summary || summary,
          freshness: summary?.freshness,
          traffic,
          searches,
          listings: { listings: listings?.listings || [] },
          leads,
          conversions,
          market: { ...market, timeOnMarket: tom },
          campaigns,
        });
      }
    } catch (e) {
      setErrorMessage(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [analyticsApi, clientViewId, dataSource, range]);

  useEffect(() => { loadGa4Status(); }, [loadGa4Status]);
  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    if (!subTabs.some((tab) => tab.id === activeSubTab)) {
      setActiveSubTab(subTabs[0]?.id || "traffic");
    }
  }, [activeSubTab, subTabs]);

  const handlePreset = useCallback((preset) => {
    setActivePreset(preset.label);
    setRange(defaultRange(preset.days));
  }, []);

  const handleRebuild = useCallback(async () => {
    setRebuilding(true);
    setMessage("");
    try {
      const body = await analyticsApi.rebuildAnalytics(clientViewId, range);
      setMessage(`Rebuilt ${body.daysProcessed} day(s) of metrics.`);
      await loadAll();
    } catch (e) { setErrorMessage(e.message); }
    finally { setRebuilding(false); }
  }, [analyticsApi, clientViewId, loadAll, range]);

  const handleGa4Sync = useCallback(async () => {
    setSyncingGa4(true);
    setMessage("");
    try {
      const body = await analyticsApi.syncGa4Analytics(clientViewId, range);
      setMessage(`Synced ${body.syncRun?.daysProcessed || 0} day(s) from Google Analytics.`);
      await loadGa4Status();
      await loadAll();
    } catch (e) { setErrorMessage(e.message); }
    finally { setSyncingGa4(false); }
  }, [analyticsApi, clientViewId, loadAll, loadGa4Status, range]);

  const ga4Configured = Boolean(ga4Status?.configured);
  const ga4LastSync = ga4Status?.lastSync;

  const handleExport = useCallback(async () => {
    setExporting(true);
    setMessage("");
    try {
      const body = await analyticsApi.exportAnalyticsReport(clientViewId, { ...range, reportType: "summary", format: "csv" });
      const blob = new Blob([body], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `analytics-${range.startDate}-${range.endDate}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
      setMessage("Report exported.");
    } catch (e) { setErrorMessage(e.message); }
    finally { setExporting(false); }
  }, [analyticsApi, clientViewId, range]);

  const handleRefreshCombined = useCallback(async () => {
    setRebuilding(true);
    setSyncingGa4(true);
    setMessage("");
    try {
      const [rebuildBody, syncBody] = await Promise.all([
        analyticsApi.rebuildAnalytics(clientViewId, range),
        ga4Configured ? analyticsApi.syncGa4Analytics(clientViewId, range) : Promise.resolve(null),
      ]);
      const rebuildDays = rebuildBody?.daysProcessed || 0;
      const syncDays = syncBody?.syncRun?.daysProcessed || 0;
      setMessage(`Refreshed Midpoint (${rebuildDays} day(s))${syncBody ? ` and GA4 (${syncDays} day(s))` : ""}.`);
      await loadGa4Status();
      await loadAll();
    } catch (e) {
      setErrorMessage(e.message);
    } finally {
      setRebuilding(false);
      setSyncingGa4(false);
    }
  }, [analyticsApi, clientViewId, ga4Configured, loadAll, loadGa4Status, range]);

  const handleSaveGa4Connection = useCallback(
    async (payload) => {
      if (typeof analyticsApi.updateGa4AnalyticsSettings !== "function") {
        setGa4ModalError("Google Analytics settings cannot be updated from this screen.");
        return;
      }
      setSavingGa4(true);
      setGa4ModalError("");
      try {
        const body = await analyticsApi.updateGa4AnalyticsSettings(clientViewId, payload);
        setGa4Status(body.dataSource || null);
        setGa4ModalOpen(false);
        setMessage("Google Analytics connection saved.");
        await loadGa4Status();
        if (dataSource === "ga4" || dataSource === "combined") {
          await loadAll();
        }
      } catch (error) {
        setGa4ModalError(error.message || "Unable to save Google Analytics settings.");
      } finally {
        setSavingGa4(false);
      }
    },
    [analyticsApi, clientViewId, dataSource, loadAll, loadGa4Status]
  );

  const freshness = allData.freshness || allData.midpoint?.freshness;
  const canConfigureGa4 = !disabled && typeof analyticsApi.updateGa4AnalyticsSettings === "function";
  const ga4Linked = Boolean(ga4Status?.measurementId || ga4Status?.propertyId);
  const ga4ConnectLabel = ga4Linked ? "Update Google Analytics" : "Connect Google Analytics";
  const openGa4Modal = () => {
    setGa4ModalError("");
    setGa4ModalOpen(true);
  };

  return (
    <div className="cv-analytics">
      {/* Toolbar */}
      <div className="cv-analytics__toolbar">
        <div className="cv-analytics__toolbar-left">
          <div className="cv-analytics__presets">
            <button
              type="button"
              className={`cv-analytics__preset${dataSource === "midpoint" ? " cv-analytics__preset--active" : ""}`}
              onClick={() => setDataSource("midpoint")}
            >
              Midpoint
            </button>
            <button
              type="button"
              className={`cv-analytics__preset${dataSource === "ga4" ? " cv-analytics__preset--active" : ""}`}
              onClick={() => setDataSource("ga4")}
            >
              Google Analytics
            </button>
            <button
              type="button"
              className={`cv-analytics__preset${dataSource === "combined" ? " cv-analytics__preset--active" : ""}`}
              onClick={() => setDataSource("combined")}
            >
              Combined
            </button>
            {RANGE_PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                className={`cv-analytics__preset${activePreset === p.label ? " cv-analytics__preset--active" : ""}`}
                onClick={() => handlePreset(p)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="cv-analytics__date-range">
            <input type="date" className="cv-analytics__date-input" value={range.startDate} onChange={(e) => { setRange((c) => ({ ...c, startDate: e.target.value })); setActivePreset(""); }} />
            <span className="cv-analytics__date-sep">to</span>
            <input type="date" className="cv-analytics__date-input" value={range.endDate} onChange={(e) => { setRange((c) => ({ ...c, endDate: e.target.value })); setActivePreset(""); }} />
          </div>
        </div>
        <div className="cv-analytics__toolbar-right">
          {canConfigureGa4 ? (
            <button
              type="button"
              className="cv-analytics__toolbar-btn cv-analytics__toolbar-btn--primary"
              onClick={openGa4Modal}
            >
              {ga4ConnectLabel}
            </button>
          ) : null}
          {dataSource === "midpoint" && !disabled ? (
            <button type="button" className="cv-analytics__toolbar-btn" onClick={handleRebuild} disabled={rebuilding}>
              {rebuilding ? "Rebuilding\u2026" : "Rebuild"}
            </button>
          ) : null}
          {dataSource === "ga4" && !disabled ? (
            <button type="button" className="cv-analytics__toolbar-btn" onClick={handleGa4Sync} disabled={syncingGa4 || !ga4Configured}>
              {syncingGa4 ? "Syncing\u2026" : "Sync GA4"}
            </button>
          ) : null}
          {dataSource === "combined" && !disabled ? (
            <button
              type="button"
              className="cv-analytics__toolbar-btn"
              onClick={handleRefreshCombined}
              disabled={rebuilding || syncingGa4}
            >
              {rebuilding || syncingGa4 ? "Refreshing\u2026" : "Refresh all"}
            </button>
          ) : null}
          {!disabled && (dataSource === "midpoint" || dataSource === "combined") ? (
            <button type="button" className="cv-analytics__toolbar-btn" onClick={handleExport} disabled={exporting}>
              {exporting ? "Exporting\u2026" : "Export CSV"}
            </button>
          ) : null}
        </div>
      </div>

      {/* Freshness */}
      {dataSource === "midpoint" && freshness ? (
        <div className={`cv-analytics__freshness${freshness.aggregationStale ? " cv-analytics__freshness--stale" : ""}`}>
          <span>Last event: {freshness.lastEventReceived ? new Date(freshness.lastEventReceived).toLocaleString() : "—"}</span>
          <span>Last aggregation: {freshness.lastAggregationRun ? new Date(freshness.lastAggregationRun).toLocaleString() : "never"}</span>
          {freshness.aggregationStale ? <StatusBadge label="Stale — rebuild recommended" tone="disputed" /> : <StatusBadge label="Fresh" tone="confirmed" />}
        </div>
      ) : null}
      {dataSource === "ga4" ? (
        <div className={`cv-analytics__freshness${ga4Configured ? "" : " cv-analytics__freshness--stale"}`}>
          <span>Measurement ID: {ga4Status?.measurementId || "—"}</span>
          <span>Property ID: {ga4Status?.propertyId || "not configured"}</span>
          <span>Last sync: {ga4LastSync?.completedAt ? new Date(ga4LastSync.completedAt).toLocaleString() : "never"}</span>
          <span>Poll interval: {ga4Status?.scheduler?.pollIntervalMinutes || ga4Status?.pollIntervalMinutes || 60} min</span>
          <span>Scheduler: {ga4Status?.scheduler?.running ? "running" : ga4Status?.scheduler?.mode || "external"}</span>
          {ga4Configured ? (
            <StatusBadge label="GA4 connected" tone="confirmed" />
          ) : (
            <StatusBadge label={ga4Status?.message || "GA4 not configured"} tone="disputed" />
          )}
        </div>
      ) : null}
      {dataSource === "combined" ? (
        <div className="cv-analytics__freshness cv-analytics__freshness--combined">
          <span>
            Midpoint aggregation:{" "}
            {freshness?.lastAggregationRun ? new Date(freshness.lastAggregationRun).toLocaleString() : "never"}
          </span>
          <span>
            GA4 last sync:{" "}
            {ga4LastSync?.completedAt ? new Date(ga4LastSync.completedAt).toLocaleString() : "never"}
          </span>
          <span>GA4 scheduler: {ga4Status?.scheduler?.running ? "running" : ga4Status?.scheduler?.mode || "external"}</span>
          <StatusBadge label="Combined view" tone="confirmed" />
        </div>
      ) : null}

      {message ? <div className="cv-analytics__message">{message}</div> : null}
      {errorMessage ? <div className="cv-analytics__error">{errorMessage}</div> : null}

      {/* Sub-tabs */}
      <nav className="cv-analytics__tabs" role="tablist">
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeSubTab === tab.id}
            className={`cv-analytics__tab${activeSubTab === tab.id ? " cv-analytics__tab--active" : ""}`}
            onClick={() => setActiveSubTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {dataSource === "ga4" && !ga4Linked && canConfigureGa4 ? (
        <div className="cv-analytics__empty">
          <p>
            This Client View is not connected to Google Analytics 4 yet. Add a Measurement ID for
            public tracking and a Property ID to sync reports here.
          </p>
          <button
            type="button"
            className="cv-analytics__toolbar-btn cv-analytics__toolbar-btn--primary"
            onClick={openGa4Modal}
          >
            Connect Google Analytics
          </button>
        </div>
      ) : null}

      {isLoading ? (
        <div className="cv-analytics__loading">
          <p>Loading analytics data&hellip;</p>
        </div>
      ) : (
        <>
          {dataSource === "combined" && activeSubTab === "traffic" ? <CombinedTrafficPane data={allData} /> : null}
          {dataSource === "combined" && activeSubTab === "engagement" ? <CombinedEngagementPane data={allData} /> : null}
          {dataSource === "combined" && activeSubTab === "properties" ? (
            <>
              <CombinedSourceNote>Properties use Midpoint listing and market intelligence. GA4 does not provide listing inventory metrics.</CombinedSourceNote>
              <PropertiesPane data={allData.midpoint} />
            </>
          ) : null}
          {dataSource === "combined" && activeSubTab === "leads" ? (
            <>
              <CombinedSourceNote>Leads and CRM outcomes use Midpoint first-party data and are not available from GA4 alone.</CombinedSourceNote>
              <LeadsPane data={allData.midpoint} />
            </>
          ) : null}
          {dataSource !== "combined" && activeSubTab === "traffic" ? <TrafficPane data={allData} /> : null}
          {dataSource === "ga4" && activeSubTab === "engagement" ? (
            <Ga4EngagementPane data={allData} />
          ) : null}
          {dataSource === "midpoint" && activeSubTab === "engagement" ? <EngagementPane data={allData} /> : null}
          {dataSource === "midpoint" && activeSubTab === "properties" ? <PropertiesPane data={allData} /> : null}
          {dataSource === "midpoint" && activeSubTab === "leads" ? <LeadsPane data={allData} /> : null}
          {dataSource === "ga4" && (activeSubTab === "properties" || activeSubTab === "leads") ? (
            <div className="cv-analytics__loading">
              <p>Properties and Leads still use Midpoint first-party data. Switch to Combined or Midpoint for listing and CRM metrics.</p>
            </div>
          ) : null}
        </>
      )}

      <StudioGa4ConnectModal
        open={ga4ModalOpen}
        status={ga4Status}
        isSaving={savingGa4}
        errorMessage={ga4ModalError}
        onClose={() => {
          if (!savingGa4) {
            setGa4ModalOpen(false);
          }
        }}
        onSave={handleSaveGa4Connection}
      />
    </div>
  );
};

export default StudioAnalyticsSection;
